import { useEffect, useRef, useState } from 'react';

interface ResultScreenProps {
  score: number;
  maxSuccessSpan: number;
  onRetry: () => void;
  onNext: () => void;
}

function getComment(score: number): string {
  if (score >= 90) return '위치와 순서를 아주 잘 기억했어요.';
  if (score >= 70) return '위치와 순서를 잘 기억했어요.';
  if (score >= 50) return '위치와 순서를 대부분 기억했어요.';
  if (score >= 40) return '위치와 순서를 절반쯤 기억했어요.';
  return '위치와 순서를 기억하는 연습을 마쳤어요.';
}

// 고양이 캐릭터 (참고 이미지와 동일한 리소스)
const CAT_IMAGE =
  'https://storage.helloreaddy.io/project_files/a1e21e58-82d2-4e64-8a34-d104a4996fee/76888ab4-7017-4ed2-b54e-aff393facf3f_compressed_-.webp';

// 원형 게이지(SVG) 치수
const RING_SIZE = 266;
const STROKE = 16;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// 결과 화면 (신한 SOL 스타일)
// - 고양이 캐릭터가 흰 카드 위에 올라앉은 구성
// - 카드 안: 결과 배지 → 원형 게이지(점수만큼 차오름) → 평가 문구 → 보조 문구
// - 점수는 Count-up, 원형 게이지는 같은 진행값으로 함께 채워진다.
export default function ResultScreen({
  score,
  maxSuccessSpan,
  onRetry,
  onNext,
}: ResultScreenProps) {
  const [displayed, setDisplayed] = useState(0);
  const [showComment, setShowComment] = useState(false);
  const frameRef = useRef<number>(0);

  const comment = getComment(score);
  const reachedText =
    maxSuccessSpan > 0 ? `최대 ${maxSuccessSpan}개 순서를 기억했어요` : '다시 한 번 도전해볼까요?';

  // 점수 Count-up + 원형 게이지 채우기 (동일 진행값 공유)
  useEffect(() => {
    const duration = 1100;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(score * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDisplayed(score);
        setShowComment(true);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [score]);

  const progressRatio = Math.min(Math.max(displayed / 100, 0), 1);
  const dashOffset = CIRCUMFERENCE * (1 - progressRatio);

  return (
    <div className="flex flex-col h-full bg-transparent px-5">
      {/* 고양이 + 카드: 내용 높이에 맞춘 카드, 남는 공간은 위쪽에 두고 하단 버튼 쪽으로 붙인다 */}
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
        <div className="mt-auto w-full flex flex-col">
          {/* 고양이 캐릭터 */}
          <div className="shrink-0 flex justify-center items-end relative z-10 animate-sh-result-cat">
            <img
              src={CAT_IMAGE}
              alt="기뻐하는 고양이 캐릭터"
              title="순서 기억하기 결과 고양이 캐릭터"
              className="w-[330px] h-[172px] object-contain object-bottom"
            />
          </div>

          {/* 결과 카드 (내용 높이에 맞춤) */}
          <div className="relative z-0 -mt-6 bg-white rounded-[28px] px-7 pt-8 pb-14 flex flex-col items-center animate-sh-result-card">
            {/* 결과 배지 */}
            <span className="inline-flex items-center rounded-full bg-primary-50 text-primary-600 text-[14px] font-semibold px-4 py-2">
              순서 기억하기 결과
            </span>

            {/* 원형 게이지 */}
            <div className="relative mt-8" style={{ width: RING_SIZE, height: RING_SIZE }}>
              <svg
                width={RING_SIZE}
                height={RING_SIZE}
                viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
                className="rotate-90"
              >
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  strokeWidth={STROKE}
                  className="stroke-background-500"
                />
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={dashOffset}
                  className="stroke-primary-500"
                />
              </svg>

              {/* 중앙 점수 */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-[92px] font-bold text-foreground-950 leading-none tabular-nums">
                  {displayed}
                </p>
                <p className="text-[18px] font-medium text-foreground-500 mt-2.5">점</p>
              </div>
            </div>

            {/* 평가 문구 (2차 정보) */}
            <p
              className={`
                text-[17px] font-bold text-foreground-950 leading-snug text-center mt-7
                transition-all duration-500
                ${showComment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
              `}
            >
              {comment}
            </p>

            {/* 보조 문구 (평가 문구보다 약하게) */}
            <p
              className={`
                text-[14px] text-foreground-500 leading-snug text-center mt-2.5
                transition-all duration-500
                ${showComment ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
              `}
            >
              {reachedText}
            </p>
          </div>
        </div>
      </div>

      {/* 하단 CTA — 다시하기(White, 좁게) / 다음(Primary, 넓게) */}
      <div className="shrink-0 flex gap-3 pt-4 pb-[calc(20px+env(safe-area-inset-bottom))]">
        <button
          onClick={onRetry}
          className="w-[37%] shrink-0 h-[58px] rounded-2xl bg-white text-foreground-600 font-semibold text-[16px] whitespace-nowrap
            active:scale-[0.99] transition-transform duration-150 hover:bg-background-100 cursor-pointer"
        >
          다시하기
        </button>
        <button
          onClick={onNext}
          className="flex-1 h-[58px] rounded-2xl bg-primary-500 text-white font-semibold text-[16px] whitespace-nowrap
            active:scale-[0.99] transition-transform duration-150 hover:bg-primary-600 cursor-pointer"
        >
          다음
        </button>
      </div>
    </div>
  );
}